export function compareDropdownKeys<T extends number | string>(key1: T, key2: T): boolean {
    if (typeof key1 === "string") {
        return key1.localeCompare(key2 as string) === 0;
    }

    return key1 === key2;
}

export function hasControlledDropdownValue<T>(
    value: T | T[] | number | string | (number | string)[] | null | undefined
): boolean {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    return value !== null && value !== undefined;
}

export function isControlledDropdownValueValid<T>(
    value: T | T[] | number | string | (number | string)[] | null | undefined,
    options: T[],
    getKey: (item: T) => number | string
): boolean {
    if (!hasControlledDropdownValue(value)) {
        return false;
    }

    const values = Array.isArray(value) ? value : [value];

    return values.every((item) => {
        const itemKey =
            typeof item === "number" || typeof item === "string" ? item : getKey(item as T);

        return options.some((option) =>
            compareDropdownKeys(getKey(option), itemKey as ReturnType<typeof getKey>)
        );
    });
}

export function getDefaultOption<T extends { is_primary?: boolean }>(options: T[]): T | undefined {
    return options.find((option) => option.is_primary) ?? (options.length === 1 ? options[0] : undefined);
}

export function getRememberedDropdownValue<T>(
    storageKey: string,
    options: T[],
    getKey: (item: T) => number | string
): T[] | undefined {
    if (!options.length) {
        return undefined;
    }

    const storedValue = localStorage.getItem(storageKey);

    if (!storedValue) {
        return undefined;
    }

    try {
        const parsedValue = JSON.parse(storedValue) as T[];
        const values = Array.isArray(parsedValue) ? parsedValue : [parsedValue];
        const matchedOptions = values
            .map((value) =>
                options.find((option) => compareDropdownKeys(getKey(option), getKey(value)))
            )
            .filter((value): value is T => value !== undefined);

        if (values.length > 0 && matchedOptions.length === values.length) {
            return matchedOptions;
        }
    } catch (error) {
        console.error(error);
    }

    localStorage.removeItem(storageKey);
    return undefined;
}
